import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Summary from '../organisms/Origination/Summary/Summary';
import { useEffect, useState, useMemo, useRef } from 'react';
import { balanceInfo, clientData } from '../types/interfaces';
import Card from '../components/card/Card';
import Tabs from '../components/tabs/Tabs';
import SummaryDocuments from '../organisms/Origination/Summary/Documents';
import SummaryAmounts from '../organisms/common/SummaryAmounts';
import Button from '../components/button/Button';
import Spinner from '../components/Spinner/spinner';
import { Toast, useToast } from '../components/toast/toast';
import {
  getApplication,
  updateApplication,
} from '../services/ApplicationService';
import {
  capitalize,
  hasData,
  hasError,
  isSuccessfulResponse,
  getEnabledRiskCodes,
} from '../utils/Utils';
import { APPLICATION_STATUS, RISK_CODES } from '../utils/Constants';
import Modal from '../components/modal/modal';
import P from '../components/texts/p/P';
import TextArea from '../components/textarea/TextArea';
import { initialClientData } from '../utils/Constants';
import Icon from '../components/icon/Icon';
import H from '../components/texts/h/H';
import Application from '../organisms/Origination/Application';
import useRole from '../hooks/useRole';
import { routes } from '../routes/routePaths';
import useStatus from '../hooks/useStatus';
import { getBalanceInfoByCode, getDebts } from '../services/RiskService';
import CheckLightResume from '../organisms/Risk/CheckLight/ChecklightResume';
import { cuitMask } from '../utils/Masks';
import { useClientTheme } from '../context/ThemeContext';
import RiskAnalysisPdf from '../organisms/Risk/Pdf/RiskAnalysisPdf';
import { useRiskPdf } from '../hooks/useRiskPdf';
import { useReferrerNames } from '../hooks/useReferrerNames';
import { useOfficers } from '../hooks/useOfficers';

const config = JSON.parse(localStorage.getItem('config') ?? '{}');
const isInstrumentationEnabled = config?.isInstrumentationEnabled ?? true;

const Committee = () => {
  const { cuit, id } = useParams();
  const { state } = useLocation();
  const { status } = state ? state : '';
  const navigate = useNavigate();
  const { clientTheme } = useClientTheme();
  const { referrerNames } = useReferrerNames();
  const { officers } = useOfficers();
  const [isClientDataLoaded, setIsClientDataLoaded] = useState<boolean>(false);
  const [clientData, setClientData] = useState<clientData>(initialClientData);
  const [isSendingApplication, setIsSendingApplication] =
    useState<boolean>(false);
  const [isRejectApplication, setIsRejectApplication] =
    useState<boolean>(false);
  const [modalText, setModalText] = useState<string>('');
  const [isSaving, _setIsSaving] = useState<boolean>(false);
  const { isComite, isCommercial, isInstrumentator } = useRole();
  const { isRejected, isFinished } = useStatus({
    applicationStatus: parseInt(status),
  });
  const [isSendApplicationModal, setIsSendApplicationModal] =
    useState<boolean>(false);
  const [applicationData, setApplicationData] = useState<any>({});
  const [riskData, setRiskData] = useState<any>([]);
  const [debts, setDebts] = useState<any>({});
  const [balanceInfo, setBalanceInfo] = useState<balanceInfo | undefined>(
    undefined
  );
  const [postBalanceData, setPostBalanceData] = useState({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const pdfRef = useRef<HTMLDivElement>(null);
  const { isGenerating: isPdfGenerating, generatePdfFromNode } = useRiskPdf({
    fileName: `analisis_riesgo_${cuit || 'cliente'}.pdf`,
  });

  const currentStatus = useMemo(() => {
    if (status) return parseInt(status);
    if (clientData?.status?.id) return clientData.status.id;
    return 0;
  }, [status, clientData?.status?.id]);

  useEffect(() => {
    if (cuit && id) {
      setIsLoading(true);
      getApplication(cuit, id).then((response) => {
        if (hasData(response)) {
          setClientData(response.data);
          setPostBalanceData(response.data.post_balance_sheet_items);
          getBalanceInfoFromCode(cuit, response.data.balance_sheet_code);

          if (response.data.risk_analysis) {
            setRiskData(response.data.risk_analysis.risks);
          }
          const code = response.data.client_debt_analysis_code;
          getDebts(cuit, code).then((response) => {
            if (hasData(response) && response.status === 200) {
              setDebts(response.data);
            } else if (hasError(response).isAnyError) {
              useToast.error({
                title: 'Ocurrió un error al consultar la información.',
                id: 'risk-toast',
                description: hasError(response).isClientError
                  ? 'Error del cliente'
                  : 'Error del servidor',
                closeButton: true,
                autoClose: 5000,
              });
            }
          });
        }
        setIsLoading(false);
      });
    }
  }, [cuit, id]);

  const getBalanceInfoFromCode = (cuit: string, balanceSheetCode: string) => {
    getBalanceInfoByCode(cuit, balanceSheetCode).then((response) => {
      if (hasData(response) && response.status === 200) {
        setBalanceInfo({
          ...response.data,
          ...formatNumbers(response.data),
        });
      } else if (hasError(response).isAnyError) {
        useToast.error({
          title: 'Ocurrió un error al consultar la información del balance.',
          id: 'risk-toast',
          description: hasError(response).isClientError
            ? 'Error del cliente'
            : 'Error del servidor',
          closeButton: true,
          autoClose: 5000,
        });
      }
    });
  };

  const handleDownloadPdf = async () => {
    await generatePdfFromNode(pdfRef.current);
  };

  const formatNumbers = (obj: Record<string, any>) => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]): [string, any] => {
        if (typeof value === 'object' && value !== null) {
          return [key, formatNumbers(value)];
        }
        return [key, value];
      })
    );
  };

  const contentTabs = [
    {
      label: 'Solicitud',
      content: (
        <>
          <Summary
            setClientData={(e: any) => setClientData(e)}
            applicationStatus={parseInt(status)}
            isSaving={isSaving}
            clientData={clientData}
            onLoadClientData={(e: boolean) => setIsClientDataLoaded(e)}
            isClientDataLoaded={isClientDataLoaded}
            showButtons={false}
          />

          {currentStatus === APPLICATION_STATUS.finished.code &&
            clientData.approved_products &&
            clientData.approved_products.length > 0 && (
              <Card margin={{ top: '4' }} width='100%'>
                <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
                  <H
                    type='3'
                    variant='neutral-1'
                    withMargin={false}
                    weight='bold'
                    modifier='strong'
                  >
                    Monto Aprobado
                  </H>
                </Card>
                <Card margin={{ top: '4' }} width='100%'>
                  <SummaryAmounts
                    clientData={clientData}
                    isManualAmount={false}
                    amounts={clientData.approved_amount || []}
                    products={clientData.approved_products}
                  />
                </Card>
              </Card>
            )}
        </>
      ),
    },
    {
      label: 'Análisis de riesgo',
      content: (
        <CheckLightResume
          data={riskData}
          showBalanceInfo={true}
          isLoading={isLoading}
          handleRiskData={(e: any) => setRiskData(e)}
          riskCodes={getEnabledRiskCodes([RISK_CODES.bpb, RISK_CODES.casfog, RISK_CODES.bcra])}
          debts={debts}
          documents={clientData.files}
          cuit={cuit}
          hideInfo={true}
          balanceData={balanceInfo}
          postBalanceData={postBalanceData}
          onDownloadPdf={handleDownloadPdf}
          isPdfLoading={isPdfGenerating}
        />
      ),
    },
    {
      label: 'Documentación completa',
      content: (
        <SummaryDocuments
          cuit={clientData.cuit}
          documents={clientData.files}
          legalEntity={clientData.basic_data.legal_entity}
          applicationId={id}
          companyName={clientData.basic_data?.company_name}
        />
      ),
    },
  ];

  const renderRejectModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Declinar una solicitud eliminará la documentación.
        </P>
        <P type='1' variant='danger' withMargin={false}>
          Esta acción no se puede deshacer
        </P>
        <P type='1' variant='neutral' withMargin={false}>
          También podés solicitar cambios. Detallalos abajo, y la solicitud
          volverá al productor con ese pedido.
        </P>
        <Card margin={{ top: '3' }}>
          <TextArea
            placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
            maxLengthHelpText={false}
            onChange={(e) => setModalText(e.target.value)}
          ></TextArea>
        </Card>
      </Card>
    );
  };

  const handleApprove = () => {
    const status = isInstrumentationEnabled 
      ? APPLICATION_STATUS.instrumentation.label 
      : APPLICATION_STATUS.finished.label;

    const useManual = applicationData.manualTotalAmounts === true;

    const formatProducts = () =>
      applicationData.applications
        ?.filter((item) => !!item)
        .map((item) => ({
          product_line_code: item.product_line_code,
          amount: {
            currency_code: item.amount.currency_code,
            value:
              typeof item.amount.value === 'string'
                ? parseFloat(item.amount.value)
                : item.amount.value,
          },
        })) || [];

    const sumAmounts = (source: any[], currency: string): number => {
      return (
        source
          ?.filter((p) => p.currency_code === currency)
          .reduce((sum, p) => {
            const value =
              typeof p.value === 'string' ? parseFloat(p.value) : p.value;
            return sum + (isNaN(value) ? 0 : value);
          }, 0) || 0
      );
    };

    const products = formatProducts();

    const baseSource = useManual
      ? applicationData.requestedAmounts
      : products.map((p) => ({
          currency_code: p.amount.currency_code,
          value: p.amount.value,
        }));

    const totalARS = parseFloat(sumAmounts(baseSource, 'ARS').toFixed(2));
    const totalUSD = parseFloat(sumAmounts(baseSource, 'USD').toFixed(2));

    const payload = {
      status_code: status,
      comment: modalText,
      approved_products: {
        products,
        manual_approved_total_amount_ars: totalARS,
        manual_approved_total_amount_usd: totalUSD,
      },
    };
    handleApplication(payload);
  };

  const handleApplication = (payload: any) => {
    setIsSendingApplication(true);
    const mapStatus = new Map();
    mapStatus.set(
      APPLICATION_STATUS.rejected.label,
      'Declinaste la solicitud de este cliente.'
    );
    mapStatus.set(
      APPLICATION_STATUS.pending.label,
      'Solicitaste cambios en la solicitud. El comercial responsable será notificado.'
    );
    mapStatus.set(
      APPLICATION_STATUS.instrumentation.label,
      'Aceptaste la documentación. El comercial responsable será notificado.'
    );
    mapStatus.set(
      APPLICATION_STATUS.finished.label,
      'Cerraste la carpeta de este cliente.'
    );

    updateApplication(cuit || '', payload, id || '').then((response) => {
      if (isSuccessfulResponse(response)) {
        useToast.success({
          title: mapStatus.get(payload.status_code),
          id: 'application-toast',
          description: '',
          closeButton: false,
          autoClose: 5000,
        });
        setTimeout(() => {
          navigate(routes.dashboard);
        }, 1000);
      } else {
        const errorTitle = payload.status_code === APPLICATION_STATUS.finished.label
          ? 'Ocurrió un error al cerrar la carpeta.'
          : 'Ocurrió un error inesperado. Intentá nuevamente.';
        
        useToast.error({
          title: errorTitle,
          id: 'application-toast',
          description: hasError(response).isClientError
            ? 'Error de cliente'
            : 'Error técnico',
          closeButton: true,
          autoClose: 5000,
        });
      }
      setIsSendingApplication(false);
    });
  };

  const sendApplicationModal = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          {isInstrumentationEnabled 
            ? 'La solicitud pasará a la instancia de Instrumentación. Podés editar los montos y los productos, y dejar un mensaje para el productor.'
            : 'La carpeta se cerrará. Podés editar los montos y los productos, y dejar un mensaje para el productor.'}
        </P>
        <Card margin={{ top: '3' }}>
          <Application
            manualTotalAmounts={clientData.manual_total_amount}
            requestedAmounts={clientData.requested_amount}
            hideInfo={true}
            applications={clientData.products}
            onChange={(e) => setApplicationData(e)}
            signers={clientData.signers}
          />

          <Card margin={{ top: '3' }}>
            <TextArea
              placeHolder='Escribí los cambios que solicitás, o el motivo de la denegación.'
              maxLengthHelpText={false}
              onChange={(e) => setModalText(e.target.value)}
            ></TextArea>
          </Card>
        </Card>
      </Card>
    );
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <RiskAnalysisPdf
        ref={pdfRef}
        clientData={clientData}
        riskData={riskData}
        balanceData={balanceInfo}
        postBalanceData={postBalanceData}
        debts={debts}
        documents={clientData.files}
        cuit={cuit}
        referrerNames={referrerNames}
        officers={officers}
      />
      {(isComite || isCommercial || isInstrumentator) && (
        <>
          <Toast id='application-toast' />
          {!isRejected && !isFinished && (
            <>
              <Modal
                isOpen={isRejectApplication}
                onClose={() => setIsRejectApplication(false)}
                title={'Declinar o solicitar cambios'}
                primaryText='Solicitar cambios'
                primaryVariant='primary'
                secondaryText='Declinar solicitud'
                secondaryVariant='secondary'
                onSecondaryAction={() => {
                  setIsRejectApplication(false);
                  handleApplication({
                    status_code: APPLICATION_STATUS.rejected.label,
                    comment: modalText,
                  });
                }}
                onPrimaryAction={() => {
                  setIsRejectApplication(false);
                  handleApplication({
                    status_code: APPLICATION_STATUS.pending.label,
                    comment: modalText,
                  });
                }}
                children={renderRejectModal()}
              />
              <Modal
                width='1000px'
                isOpen={isSendApplicationModal}
                onClose={() => setIsSendApplicationModal(false)}
                title={'Aprobar solicitud'}
                primaryText='Aprobar solicitud'
                primaryVariant='primary'
                secondaryText='Cancelar'
                secondaryVariant='secondary'
                onSecondaryAction={() => {
                  setIsSendApplicationModal(false);
                }}
                onPrimaryAction={() => {
                  setIsSendApplicationModal(false);
                  handleApprove();
                }}
                children={sendApplicationModal()}
              />
            </>
          )}
          <Card display='flex' margin={{ left: '2', bottom: '4' }} gap='10px'>
            <Icon
              name='chevron_left_outline'
              height={24}
              width={24}
              cursor='pointer'
              onClick={() => window.history.back()}
            />
            <H withMargin={false} type='2' weight='bold' modifier='strong'>
              Solicitud de {capitalize(clientData?.basic_data?.company_name)} ({' '}
              {cuitMask(clientData.cuit)} )
            </H>
          </Card>
          {(() => {
            const isRiskTabDisabled =
              !riskData || (Array.isArray(riskData) && riskData.length === 0);
            return (
              <Tabs
                tabs={contentTabs}
                disabledTabs={isRiskTabDisabled ? [1] : []}
              />
            );
          })()}
          <Card display='flex' justifyContent='flex-end' margin={{ top: '4' }}>
            {!isRejected && !isFinished && (
              <Card width='280px'>
                <Button
                  id='application-button-reject'
                  size='lg'
                  variant='secondary'
                  onClick={() => setIsRejectApplication(true)}
                  fullWidth
                  disabled={isSendingApplication}
                >
                  {isSendingApplication ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Declinar o solicitar cambios'
                  )}
                </Button>
              </Card>
            )}
            <Card width='180px' margin={{ left: '4' }}>
              <Button
                id='client-button-approve'
                size='lg'
                variant='primary'
                onClick={() => setIsSendApplicationModal(true)}
                fullWidth
                disabled={isSendingApplication}
              >
                {isSendingApplication ? (
                  <Spinner size='sm' variant='primary' />
                ) : (
                  'Aprobar'
                )}
              </Button>
            </Card>
          </Card>
        </>
      )}
    </Card>
  );
};

export default Committee;
